import { useEffect, useState, useCallback, useRef } from 'react';

import {
  StatsigFeatureKeys,
  StatsigFeatureKeyType,
} from '@atlassian/bitbucket-features';

import { useStatsigGate } from 'src/contexts/feature-provider';
import { captureExceptionWithTags } from 'src/utils/sentry';

// The consumer can pass in the gate type so we can reuse this code
// for both rollouts. All error sections will be rolled out with M1 and flags will be rolled out
// a few weeks later.
type ErrorViewType = 'error-section' | 'error-flag';

// Define a mapping of ErrorViewType to StatsigFeatureKeys
const SentryFeatureGates: Record<ErrorViewType, StatsigFeatureKeyType> = {
  'error-section': StatsigFeatureKeys.bbcSentryIdsInErrorUiM1,
  'error-flag': StatsigFeatureKeys.bbcSentryIdsInErrorUiFlags,
};

// This hook returns a util function that handles checking the Sentry related feature flag and
// capturing exceptions in Sentry. Regardless of the flag state it will capture the event in Sentry.
// If flag is enabled, it returns the Sentry ID, otherwise it returns an empty string.
export const useCaptureSentryException = ({
  errorViewType,
}: {
  errorViewType: ErrorViewType;
}) => {
  const isSentryIdEnabledForViewType: boolean = useStatsigGate(
    SentryFeatureGates[errorViewType]
  );

  const captureSentryException = useCallback(
    async (errorToCapture: Error): Promise<string> => {
      // capture an event in sentry regardless of the feature flag
      const sentryId = await captureExceptionWithTags(errorToCapture, {
        visibleToUser: isSentryIdEnabledForViewType.toString(),
        viewType: errorViewType,
      });

      // only return the Sentry ID if the ft gate is enabled
      return isSentryIdEnabledForViewType ? sentryId : '';
    },
    [errorViewType, isSentryIdEnabledForViewType]
  );

  return { captureSentryException };
};

// This hook uses the `useCaptureSentryException` hook to capture an exception
// automatically and handles storing the Sentry ID and returning it.
export const useSentryExceptionId = ({
  error,
  errorViewType,
}: {
  error?: Error;
  errorViewType: ErrorViewType;
}): string => {
  const [sentryId, setSentryId] = useState<string>('');
  const { captureSentryException } = useCaptureSentryException({
    errorViewType,
  });

  // used to track the last error so we don't capture the same one multiple times
  const lastErrorRef = useRef<Error | undefined>(undefined);

  useEffect(() => {
    (async () => {
      if (error && error !== lastErrorRef.current) {
        // Update the ref to the current error
        lastErrorRef.current = error;
        const id = await captureSentryException(error);
        setSentryId(id);
      }
    })();
  }, [error, captureSentryException]);

  return sentryId;
};
